import { useEffect, useState } from "react";
import { useParams, Link, useLocation, Navigate } from "react-router-dom";
import { ArrowUpRight, ArrowLeft, CheckCircle2, Phone, ZoomIn } from "lucide-react";
import { Reveal, SectionLabel } from "../components/Motion";
import SEO from "../components/SEO";
import Lightbox from "../components/Lightbox";
import { getService, SERVICE_GROUPS, getServicesByGroup } from "../data/services";

export default function ServiceDetail({ groupKey }) {
  const { slug } = useParams();
  const { pathname } = useLocation();
  const service = getService(slug);
  const [lightboxIndex, setLightboxIndex] = useState(null);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
    setLightboxIndex(null);
  }, [pathname]);

  if (!service || service.group !== groupKey) {
    return <Navigate to="/services" replace />;
  }

  const Icon = service.icon;
  const group = SERVICE_GROUPS[groupKey];
  const siblings = getServicesByGroup(groupKey).filter((s) => s.slug !== slug);
  const rawGallery = service.gallery && service.gallery.length > 0 ? service.gallery : [service.image];
  const gallery = rawGallery.filter(Boolean);
  const hasImages = gallery.length > 0;

  return (
    <div data-testid={`page-service-${service.slug}`}>
      <SEO
        title={`${service.title} à Mulhouse — ${group.label}`}
        description={`${service.title} par Euro Chape à Illzach près de Mulhouse. ${service.tagline || service.summary || "Prestation d'artisan pour vos sols en Alsace et Territoire de Belfort."} Devis gratuit sous 48h.`}
        keywords={`${service.title.toLowerCase()} Mulhouse, ${service.title.toLowerCase()} Alsace, ${service.title.toLowerCase()} Illzach, chapiste Haut-Rhin`}
        canonical={`https://eurochape.com${pathname}`}
      />
      {/* HERO */}
      <section className="pt-32 md:pt-40 pb-16 md:pb-24 px-6 md:px-12 lg:px-24 bg-[#EBEBE6] border-b border-[#D5D5D0]">
        <Reveal>
          <Link
            to="/services"
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] font-semibold text-[#1A2D7E] hover:text-[#F08722] mb-10"
          >
            <ArrowLeft className="w-4 h-4" /> Retour aux services
          </Link>
          <SectionLabel>{group.label}</SectionLabel>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mt-8 items-end">
            <div className="lg:col-span-8">
              <h1 className="font-display font-black uppercase tracking-tighter text-5xl sm:text-7xl lg:text-[6rem] leading-[0.88] text-[#1A2D7E]">
                {service.title}
              </h1>
              <p className="mt-6 text-xl sm:text-2xl font-light text-[#F08722] italic">
                {service.tagline}
              </p>
            </div>
            <div className="lg:col-span-4 flex justify-end">
              <div className="w-24 h-24 md:w-32 md:h-32 bg-[#1A2D7E] flex items-center justify-center">
                <Icon className="w-12 h-12 md:w-16 md:h-16 text-[#F08722]" strokeWidth={1.5} />
              </div>
            </div>
          </div>
        </Reveal>
      </section>

      {/* IMAGE + DESCRIPTION */}
      <section className="px-6 md:px-12 lg:px-24 py-20 md:py-28">
        <div className={`grid grid-cols-1 ${hasImages ? "lg:grid-cols-12" : ""} gap-10 lg:gap-16`}>
          {hasImages && (
            <Reveal className="lg:col-span-6">
              <button
                type="button"
                onClick={() => setLightboxIndex(0)}
                data-testid="service-main-image-btn"
                className="group relative block w-full aspect-[4/5] overflow-hidden border border-[#D5D5D0] cursor-zoom-in"
                aria-label="Agrandir la photo"
              >
                <img
                  src={gallery[0]}
                  alt={service.title}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute top-4 right-4 w-10 h-10 bg-[#1A2D7E] text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                  <ZoomIn className="w-5 h-5" />
                </div>
              </button>
              {gallery.length > 1 && (
                <div className="grid grid-cols-2 gap-3 mt-3">
                  {gallery.slice(1).map((src, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => setLightboxIndex(i + 1)}
                      data-testid={`service-thumb-${i + 1}`}
                      className="group relative aspect-[4/3] overflow-hidden border border-[#D5D5D0] cursor-zoom-in"
                      aria-label="Agrandir la photo"
                    >
                      <img
                        src={src}
                        alt={`${service.title} ${i + 2}`}
                        loading="lazy"
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-[#1A2D7E]/0 group-hover:bg-[#1A2D7E]/20 transition-colors flex items-center justify-center">
                        <ZoomIn className="w-5 h-5 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </Reveal>
          )}
          <Reveal className={hasImages ? "lg:col-span-6 lg:pl-4" : "max-w-4xl"} delay={0.1}>
            <SectionLabel>Présentation</SectionLabel>
            <h2 className="font-display font-bold uppercase tracking-tighter text-3xl sm:text-4xl mt-6 leading-[0.98] text-[#1A2D7E]">
              {service.short}
            </h2>
            <p className="mt-8 text-base sm:text-lg font-light leading-relaxed text-[#1A2D7E]">
              {service.description}
            </p>
            <Link
              to="/devis"
              data-testid="service-devis-cta"
              className="mt-10 inline-flex items-center gap-3 bg-[#F08722] text-white px-8 py-4 text-xs uppercase tracking-[0.18em] font-semibold hover:bg-[#1A2D7E] transition-colors"
            >
              Demander un devis <ArrowUpRight className="w-4 h-4" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* SECTIONS */}
      <section className="px-6 md:px-12 lg:px-24 py-20 md:py-28 bg-[#EBEBE6] border-y border-[#D5D5D0]">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#D5D5D0] border border-[#D5D5D0]">
          {service.sections.map((sec, i) => (
            <Reveal key={sec.title} delay={i * 0.06}>
              <div className="bg-[#F4F4F0] p-8 md:p-10 h-full">
                <div className="text-xs font-mono text-[#F08722]">/ 0{i + 1}</div>
                <h3 className="font-display font-bold uppercase tracking-tight text-xl mt-4 text-[#1A2D7E]">
                  {sec.title}
                </h3>
                <ul className="mt-6 space-y-3">
                  {sec.items.map((it) => (
                    <li key={it} className="flex items-start gap-3 text-sm text-[#1A2D7E]">
                      <CheckCircle2 className="w-4 h-4 mt-0.5 text-[#F08722] flex-shrink-0" />
                      <span>{it}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* CTA INLINE */}
      <section className="px-6 md:px-12 lg:px-24 py-20 md:py-24">
        <Reveal>
          <div className="bg-[#1A2D7E] text-white p-10 md:p-16 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-8">
              <div className="text-xs uppercase tracking-[0.22em] text-[#F08722] font-semibold mb-3">
                Un projet de {service.title.toLowerCase()} ?
              </div>
              <h3 className="font-display font-bold uppercase tracking-tighter text-3xl md:text-4xl leading-[0.95]">
                Contactez Euro Chape pour un devis sur mesure.
              </h3>
            </div>
            <div className="lg:col-span-4 flex flex-col gap-3">
              <Link
                to="/devis"
                className="inline-flex items-center justify-between bg-[#F08722] text-white px-6 py-4 text-xs uppercase tracking-[0.18em] font-semibold hover:bg-white hover:text-[#1A2D7E] transition-colors"
              >
                Demander un devis <ArrowUpRight className="w-4 h-4" />
              </Link>
              <a
                href="tel:+33767945459"
                className="inline-flex items-center justify-between border border-white/40 text-white px-6 py-4 text-xs uppercase tracking-[0.18em] font-semibold hover:border-[#F08722] hover:text-[#F08722] transition-colors"
              >
                <span className="flex items-center gap-2"><Phone className="w-4 h-4" /> 07 67 94 54 59</span>
              </a>
            </div>
          </div>
        </Reveal>
      </section>

      {/* OTHERS IN GROUP */}
      {siblings.length > 0 && (
        <section className="px-6 md:px-12 lg:px-24 py-20 md:py-28 border-t border-[#D5D5D0]">
          <Reveal>
            <SectionLabel>Voir aussi</SectionLabel>
            <h2 className="font-display font-bold uppercase tracking-tighter text-3xl sm:text-4xl mt-6 leading-[0.98] text-[#1A2D7E]">
              Autres prestations « {group.label.toLowerCase()} »
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#D5D5D0] border border-[#D5D5D0] mt-12">
            {siblings.map((s, i) => {
              const SIcon = s.icon;
              return (
                <Reveal key={s.slug} delay={i * 0.06}>
                  <Link
                    to={`${group.basePath}/${s.slug}`}
                    className="block bg-[#F4F4F0] p-8 hover:bg-[#1A2D7E] hover:text-white transition-colors group h-full"
                  >
                    <div className="flex items-start justify-between">
                      <SIcon className="w-9 h-9 text-[#F08722]" strokeWidth={1.5} />
                      <ArrowUpRight className="w-5 h-5 text-[#1A2D7E] group-hover:text-white" />
                    </div>
                    <h3 className="font-display font-bold uppercase tracking-tight text-2xl mt-10">
                      {s.title}
                    </h3>
                    <p className="mt-3 text-sm font-light text-[#5C5E58] group-hover:text-[#E6E6E0] leading-relaxed">
                      {s.short}
                    </p>
                  </Link>
                </Reveal>
              );
            })}
          </div>
        </section>
      )}

      <Lightbox
        images={gallery}
        index={lightboxIndex}
        onClose={() => setLightboxIndex(null)}
        onPrev={() => setLightboxIndex((i) => (i - 1 + gallery.length) % gallery.length)}
        onNext={() => setLightboxIndex((i) => (i + 1) % gallery.length)}
      />
    </div>
  );
}
